import React, { createContext, useContext, useState, useEffect } from 'react';
import { Item } from '../types';

// Define the cart item structure
export interface CartItem {
  item: Item;
  quantity: number;
}

// Define the context type
interface CartContextType {
  cart: CartItem[];
  addToCart: (item: Item, quantity: number, incremental?: boolean) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
}

// Create context with default values
const CartContext = createContext<CartContextType>({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  getCartTotal: () => 0,
});

// Export custom hook for using the cart context
export const useCart = () => useContext(CartContext);

// Provider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  // Load cart from localStorage on component mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (error) {
        console.error('Failed to parse cart from localStorage:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Add item to cart
  // incremental = true: add quantity to existing item quantity (default behavior)
  // incremental = false: set the quantity to the specified value 
  const addToCart = (item: Item, quantity: number, incremental: boolean = false) => {
    setCart(prevCart => {
      // Check if item already exists in cart
      const existingItemIndex = prevCart.findIndex(cartItem => cartItem.item.id === item.id);
      
      if (existingItemIndex !== -1) {
        // Item exists, update quantity
        const updatedCart = [...prevCart];
        if (incremental) {
          // Add the new quantity to the existing quantity
          updatedCart[existingItemIndex].quantity += quantity;
        } else {
          // Set the quantity to the specified value
          updatedCart[existingItemIndex].quantity = quantity;
        }
        return updatedCart;
      } else {
        // Item doesn't exist, add new item
        return [...prevCart, { item, quantity }];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (itemId: string) => {
    setCart(prevCart => prevCart.filter(cartItem => cartItem.item.id !== itemId));
  };

  // Update item quantity
  const updateQuantity = (itemId: string, quantity: number) => {
    setCart(prevCart => {
      if (quantity <= 0) {
        // If quantity is 0 or negative, remove item
        return prevCart.filter(cartItem => cartItem.item.id !== itemId);
      }
      
      // Update quantity for the item
      return prevCart.map(cartItem => 
        cartItem.item.id === itemId 
          ? { ...cartItem, quantity } 
          : cartItem
      );
    });
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Calculate total price of items in cart
  const getCartTotal = () => {
    return cart.reduce((total, cartItem) => {
      return total + (parseFloat(cartItem.item.price) * cartItem.quantity);
    }, 0);
  };

  return (
    <CartContext.Provider 
      value={{ 
        cart, 
        addToCart, 
        removeFromCart, 
        updateQuantity,
        clearCart,
        getCartTotal
      }}
    >
      {children}
    </CartContext.Provider>
  );
}; 